import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { CheckboxComponent } from '@3ddv/software-division-components/backoffice/checkbox';
import { PaginationComponent as SdcPaginationComponent } from '@3ddv/software-division-components/backoffice/pagination';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { GsLoaderComponent } from '../loader/gs-loader.component';

export interface SelectionMeta {
  totalAvailable: number;
  selectedTotal: number;
  isAllSelected: boolean;
}

@Component({
  selector: 'gs-selectable-list',
  templateUrl: './gs-selectable-list.component.html',
  styleUrls: ['./gs-selectable-list.component.css'],
  imports: [
    CheckboxComponent,
    InputComponent,
    SdcPaginationComponent,
    GsLoaderComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GsSelectableListComponent),
      multi: true,
    },
  ],
})
export class GsSelectableListComponent<T extends object>
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @Input() items: T[] = [];
  @Input() idKey: keyof T = 'id' as keyof T;
  @Input() titleKey: keyof T = 'name' as keyof T;
  @Input() subtitleKey?: keyof T;
  @Input() imageKey?: keyof T;
  @Input() mode: 'single' | 'multiple' = 'multiple';
  @Input() pageSize = 10;
  @Input() searchable = true;
  @Input() disabledIds: number[] = [];
  @Input() disabled = false;
  @Input() serverSide = false;
  @Input() page = 1;
  @Input() totalItems: number | null = null;
  @Input() loading = false;

  @Output() selectionMeta = new EventEmitter<SelectionMeta>();
  @Output() pageChange = new EventEmitter<number>();

  public searchControl = new FormControl('', { nonNullable: true });
  public currentPage = 1;

  private selectedIds: number[] = [];
  private rowControls = new Map<number, FormControl<boolean>>();
  private subscriptions: Subscription[] = [];
  private isSynchronizingControls = false;
  private isDisabled = false;

  private onChange: (ids: number[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor() {
    const searchSubscription = this.searchControl.valueChanges.subscribe(() => {
      this.currentPage = 1;
    });

    this.subscriptions.push(searchSubscription);
  }

  ngOnChanges(): void {
    this.synchronizeRowControls();
    this.emitSelectionMeta();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  writeValue(value: number[] | null): void {
    this.selectedIds = this.normalizeIds(value ?? []);
    this.synchronizeRowControls();
    this.emitSelectionMeta();
  }

  registerOnChange(fn: (ids: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
    this.synchronizeRowControls();
  }

  get visibleItems(): T[] {
    const filteredItems = this.filteredItems;

    if (this.serverSide) {
      return filteredItems;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    return filteredItems.slice(startIndex, endIndex);
  }

  get paginationTotal(): number {
    if (this.serverSide) {
      return this.totalItems ?? this.items.length;
    }

    return this.filteredItems.length;
  }

  get paginationPage(): number {
    return this.serverSide ? this.page : this.currentPage;
  }

  get filteredItems(): T[] {
    const searchText = this.searchControl.value.trim().toLowerCase();

    if (!this.searchable || !searchText) {
      return this.items;
    }

    return this.items.filter(item => {
      const title = this.getText(item, this.titleKey).toLowerCase();
      const subtitle = this.subtitleKey
        ? this.getText(item, this.subtitleKey).toLowerCase()
        : '';

      return title.includes(searchText) || subtitle.includes(searchText);
    });
  }

  public checkboxControl(item: T): FormControl<boolean> {
    const itemId = this.getItemId(item);
    const existingControl = this.rowControls.get(itemId);

    if (existingControl) {
      return existingControl;
    }

    const control = new FormControl(this.selectedIds.includes(itemId), {
      nonNullable: true,
    });
    const subscription = control.valueChanges.subscribe(isSelected => {
      this.updateSelection(itemId, isSelected);
    });

    this.rowControls.set(itemId, control);
    this.synchronizeControlDisabledState(control, itemId);
    this.subscriptions.push(subscription);

    return control;
  }

  public checkboxName(item: T): string {
    return `gs-selectable-list-${this.getItemId(item)}`;
  }

  public getItemId(item: T): number {
    const idValue = this.getProperty(item, this.idKey);
    const itemId = Number(idValue);

    return Number.isFinite(itemId) ? itemId : 0;
  }

  public itemTitle(item: T): string {
    return this.getText(item, this.titleKey);
  }

  public itemSubtitle(item: T): string {
    if (!this.subtitleKey) {
      return '';
    }

    return this.getText(item, this.subtitleKey);
  }

  public itemImage(item: T): string {
    if (!this.imageKey) {
      return '';
    }

    const image = this.getProperty(item, this.imageKey);
    return typeof image === 'string' ? image : '';
  }

  public isItemDisabled(item: T): boolean {
    const itemId = this.getItemId(item);
    return (
      this.isDisabled || this.disabled || this.disabledIds.includes(itemId)
    );
  }

  public onRowClick(event: MouseEvent, item: T): void {
    const target = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (
      target.closest('sdc-checkbox, .selectable-list-title') ||
      this.isItemDisabled(item)
    ) {
      return;
    }

    const control = this.checkboxControl(item);
    control.setValue(!control.value);
  }

  public onPageChange(page: number): void {
    if (this.serverSide) {
      this.pageChange.emit(page);
      return;
    }

    this.currentPage = page;
  }

  private updateSelection(itemId: number, isSelected: boolean): void {
    if (this.isSynchronizingControls || this.disabledIds.includes(itemId)) {
      return;
    }

    if (this.mode === 'single') {
      this.selectedIds = isSelected ? [itemId] : [];
    } else if (isSelected && !this.selectedIds.includes(itemId)) {
      this.selectedIds = [...this.selectedIds, itemId];
    } else if (!isSelected) {
      this.selectedIds = this.selectedIds.filter(id => id !== itemId);
    } else {
      return;
    }

    this.synchronizeRowControls();
    this.onChange([...this.selectedIds]);
    this.onTouched();
    this.emitSelectionMeta();
  }

  private synchronizeRowControls(): void {
    this.isSynchronizingControls = true;

    this.items.forEach(item => {
      const itemId = this.getItemId(item);
      const control = this.checkboxControl(item);
      control.setValue(this.selectedIds.includes(itemId));
      this.synchronizeControlDisabledState(control, itemId);
    });

    this.isSynchronizingControls = false;
  }

  private emitSelectionMeta(): void {
    const totalAvailable = this.totalItems ?? this.items.length;
    const selectedTotal = this.selectedIds.length;
    const isAllSelected =
      totalAvailable > 0 && selectedTotal === totalAvailable;

    this.selectionMeta.emit({ totalAvailable, selectedTotal, isAllSelected });
  }

  private synchronizeControlDisabledState(
    control: FormControl<boolean>,
    itemId: number
  ): void {
    const shouldBeDisabled =
      this.isDisabled || this.disabled || this.disabledIds.includes(itemId);

    if (shouldBeDisabled && control.enabled) {
      control.disable({ emitEvent: false });
      return;
    }

    if (!shouldBeDisabled && control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  private normalizeIds(ids: number[]): number[] {
    const numericIds = ids
      .map(id => Number(id))
      .filter(id => Number.isFinite(id));
    return Array.from(new Set(numericIds));
  }

  private getText(item: T, key: keyof T): string {
    const value = this.getProperty(item, key);
    return value === null || value === undefined ? '' : String(value);
  }

  private getProperty(item: T, key: keyof T): unknown {
    return (item as Record<keyof T, unknown>)[key];
  }
}
